import React, { lazy, Suspense } from 'react';
import { Routes, Route, Navigate } from 'react-router';
import { CircularProgress, Box } from '@mui/material';
import { QueryClientProvider } from '@tanstack/react-query';
import { queryClient } from './shared/api/queryClient';
import { AuthProvider } from './features/auth';
import { InvestigationProvider } from './features/investigation';
import { ProtectedRoute } from './features/auth';
import { GamificationLayer } from './features/gamification';
import { useFeatureSwitch } from './shared/hooks/useFeatureSwitch';


// Lazy load route components for code splitting
const LoginPage = lazy(() => import('./features/auth').then(m => ({ default: m.LoginPage })));
const CustomerSelectionPage = lazy(() => import('./features/customer').then(m => ({ default: m.CustomerSelectionPage })));
const InvestigationDashboard = lazy(() => import('./features/investigation').then(m => ({ default: m.InvestigationDashboard })));
const PlayerProfilePage = lazy(() => import('./features/gamification').then(m => ({ default: m.PlayerProfilePage })));

// Loading fallback component
const LoadingFallback: React.FC = () => (
  <Box
    sx={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: '100dvh',
      bgcolor: 'background.default',
    }}
  >
    <CircularProgress size={48} />
  </Box>
);

// ProtectedRoute (from features/auth) handles auth-only routing.
// ProtectedRouteWithContext adds InvestigationProvider for customer-scoped pages.
const ProtectedRouteWithContext: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  return (
    <ProtectedRoute>
      <InvestigationProvider>
        {children}
      </InvestigationProvider>
    </ProtectedRoute>
  );
};

const GamificationGuard: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const enabled = useFeatureSwitch('gamification');
  if (!enabled) return <Navigate to="/" replace />;
  return <>{children}</>;
};

const App: React.FC = () => {
  return (
    <QueryClientProvider client={queryClient}>
    <AuthProvider>
      <GamificationLayer>
        <Suspense fallback={<LoadingFallback />}>
          <Routes>
            <Route path="/login" element={<LoginPage />} />
            <Route
              path="/"
              element={
                <ProtectedRouteWithContext>
                  <CustomerSelectionPage />
                </ProtectedRouteWithContext>
              }
            />
            <Route
              path="/customers/:customerId"
              element={
                <ProtectedRouteWithContext>
                  <InvestigationDashboard />
                </ProtectedRouteWithContext>
              }
            />
            <Route
              path="/profile"
              element={
                <ProtectedRoute>
                  <GamificationGuard>
                    <PlayerProfilePage />
                  </GamificationGuard>
                </ProtectedRoute>
              }
            />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </Suspense>
      </GamificationLayer>
    </AuthProvider>
    </QueryClientProvider>
  );
};

export default App;
